import { decodeJwtPayload } from "../context/AuthContext";
import { dismissSubscriptionExpiredDialog } from "./subscriptionExpiredDialog";

// Shared impersonation session helpers. Two callers today:
//   - <ImpersonationBanner /> uses restoreAdminSession + consumeReturnToPath
//     in its manual-stop / auto-expire teardown.
//   - The global axios 401 interceptor (`src/api/api.js`) uses the same
//     helpers plus isImpersonationToken() to distinguish "my impersonation
//     JWT expired mid-request" from a plain admin sign-out — so an expired
//     shadow token gracefully restores the admin session instead of blowing
//     both identities away and dumping the user on the login page.
//
// Storage keys (owned by ImpersonateUserModal at stash time — do NOT rename
// without updating that file too):
//   sessionStorage.impersonator_original_token
//   sessionStorage.impersonator_original_refresh
//   sessionStorage.impersonator_original_user
//   sessionStorage.impersonation.returnTo

// Restores the admin session that was stashed before impersonation. Returns
// (a promise of) the parsed admin user object on success, or null when
// there's nothing to restore (e.g. the tab was opened mid-session and never
// had a stash).
//
// ASYNC because the stashed ACCESS token is usually dead on arrival: access
// tokens live 15 minutes, an impersonation session up to 60. Restoring the
// stale token as-is meant the admin's very next API call got a 401, and the
// global interceptor — finding no impersonation claim and no stash left —
// took its standard path and signed the admin all the way out. So when the
// stashed access token has expired and a refresh token exists, a fresh
// access token is minted BEFORE the caller navigates. Plain fetch, not the
// axios instance: a failed refresh must not re-enter the 401 interceptor.
export const restoreAdminSession = async () => {
  const token = sessionStorage.getItem("impersonator_original_token");
  const refresh = sessionStorage.getItem("impersonator_original_refresh");
  const userJson = sessionStorage.getItem("impersonator_original_user");
  if (!token) return null;

  localStorage.setItem("authToken", token);
  if (refresh) localStorage.setItem("refreshToken", refresh);
  if (userJson) localStorage.setItem("user", userJson);

  // Imperative dialogs survive the soft navigation out of impersonation —
  // an upgrade dialog raised while acting as an expired tenant would
  // otherwise still be standing on the ADMIN's own screen. The admin's
  // identity is not expired; whatever was on screen belonged to the session
  // that just ended.
  dismissSubscriptionExpiredDialog();

  const payload = decodeJwtPayload(token);
  const accessExpired = payload?.exp && payload.exp * 1000 <= Date.now();
  if (accessExpired && refresh) {
    try {
      const resp = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/auth/refresh`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: refresh }),
        }
      );
      if (resp.ok) {
        const data = await resp.json();
        const fresh = data?.access_token || data?.data?.access_token;
        if (fresh) localStorage.setItem("authToken", fresh);
      }
      // Non-OK (refresh revoked / rotated by a newer login elsewhere) falls
      // through with the stale token installed — the caller's normal 401
      // handling then applies, which is an honest sign-out rather than a
      // silent one.
    } catch {
      /* network hiccup — same fall-through as above */
    }
  }

  // The impersonated tenant user may have been read-only, in which case the
  // dashboard wrote `readOnlyMode` while acting as them. That flag is only
  // ever cleared on logout, so without this it followed the ADMIN back into
  // their own session and greyed out the whole product for someone who is
  // not read-only at all. Drop it here and let the admin's own dashboard
  // load decide afresh.
  localStorage.removeItem("readOnlyMode");

  sessionStorage.removeItem("impersonator_original_token");
  sessionStorage.removeItem("impersonator_original_refresh");
  sessionStorage.removeItem("impersonator_original_user");

  try {
    return userJson ? JSON.parse(userJson) : null;
  } catch {
    return null;
  }
};

// Validates the stashed return-to path before handing it to react-router. If
// the value is missing, malformed, or points outside `/admin/`, fall back to
// the admin home so a corrupted sessionStorage value can't redirect into a
// tenant route after impersonation ends.
export const consumeReturnToPath = () => {
  const raw = sessionStorage.getItem("impersonation.returnTo");
  sessionStorage.removeItem("impersonation.returnTo");
  if (typeof raw !== "string" || raw.length === 0) return "/admin";
  if (raw === "/admin" || raw.startsWith("/admin/") || raw.startsWith("/admin?")) {
    return raw;
  }
  return "/admin";
};

// True when the given JWT carries an `impersonating` claim — the shadow-
// token marker that admin.audit's start endpoint stamps on. Used by the
// 401 interceptor to tell "impersonation token expired" apart from a real
// admin sign-out.
export const isImpersonationToken = (token) =>
  Boolean(decodeJwtPayload(token)?.impersonating);

// True when the admin's original session is available for restoration.
// Cheap check — a bare presence read of the stash's minimum key.
export const hasAdminStash = () =>
  Boolean(sessionStorage.getItem("impersonator_original_token"));
